<template>
  <CardWrapper
    class="wallet-widget"
    data-test-id="wallet-widget"
    :title="i18n.dash_wallet()"
  >
    <div class="wallet-widget__balance">
      <p data-test-id="wallet-widget-common-balance">
        {{ tronSum }} <span class="currency">USDT/USDC</span>
      </p>
    </div>
    <SwitchWrapper
      class="wallet-zero-switch"
      :label="i18n.lbl_zeros()"
      @switchChange="hideZeroBalances = $event"
    />
    <div
      class="wallet-widget__balances"
      data-test-id="wallet-widget-balances-list"
    >
      <div
        :class="[
          'wallet-widget__balances-item',
          { active: activeBalance === bl.key }
        ]"
        :data-test-id="`wallet-widget-balances-item-${bl.key}`"
        v-for="bl in showedBalances"
        :key="`balance-${bl.key}`"
        @click="toggleBalance(bl.key)"
      >
        <div class="wallet-widget__balances-item-header">
          <p
            class="text_medium"
            data-test-id="wallet-widget-balances-item-coin"
          >
            {{ bl.formated }}
          </p>
          <p
            class="text_medium amount"
            data-test-id="wallet-widget-balances-item-amount"
          >
            {{ bl.balance }}
          </p>
        </div>
        <AccordeonWrapper>
          <div
            v-show="activeBalance === bl.key"
            class="wallet-widget__balances-item-details"
            data-test-id="wallet-widget-balances-item-details"
          >
            <p data-test-id="wallet-widget-balances-item-white">
              {{i18n.white_wallet()}}
              <span data-test-id="wallet-widget-balances-item-white-amount">
                {{ balance }}
              </span>
            </p>
            <p data-test-id="wallet-widget-balances-item-gray">
              {{i18n.gray_wallet()}}
              <span data-test-id="wallet-widget-balances-item-gray-amount">
                {{ greyTronValue }}
              </span>
            </p>
          </div>
        </AccordeonWrapper>
      </div>
    </div>
    <div
      class="wallet-widget__btn-container"
      data-test-id="wallet-widget-btn-container"
    >
      <RoundButton
        :label="i18n.lbl_refill()"
        :disabled="isDisabledTransferableBtn"
        testId="wallet-widget-top-up-btn"
        icon="/static/img/banknote-arrow-up.svg"
        @click="$emit(events.walletTopUp, currentAsset)"
      />

      <RoundButton
        :label="i18n.lbl_withdraw()"
        :disabled="isDisabledTransferableBtn || !isShowWithdrawal"
        testId="wallet-widget-withdrawal-btn"
        icon="/static/img/banknote-arrow-down.svg"
        @click="$emit(events.withdrawalFromWallet, currentAsset)"
      />

      <RoundButton
        :label="i18n.lbl_exchange()"
        :disabled="true"
        testId="wallet-widget-exchange-btn"
        icon="/static/img/arrow-right-left.svg"
      />

      <RoundButton
        :label="i18n.lbl_update()"
        :disabled="isLoading"
        testId="wallet-widget-refresh-btn"
        icon="autorenew"
        @click="refreshWallet"
      />
    </div>
  </CardWrapper>
</template>

<script>
  import { i18n } from '@js/i18n'
  import { commonEvents } from '@js/events/common_events'
  import { CRYPTO_CURRENCYS_USDT, isNetworkToken, isCryptoToken, formatTokenCode, sortAssetCodes } from '@utils/consts'
  import CurrencyRound from '@com/mixins/currency.round.mixin'
  import { mapGetters, mapActions } from 'vuex'
  import { vuexTypes } from '@vuex/types'
  import get from 'lodash/get'

  import CardWrapper from '@com/UI/CardWrapper.vue'
  import RoundButton from '@com/UI/RoundBtn.vue'
  import AccordeonWrapper from '@com/UI/AccordeonWrapper.vue'
  import SwitchWrapper from '@com/UI/SwitchWrapper.vue'

  export default {
    name: 'wallet-widget',
    components: {
      CardWrapper,
      RoundButton,
      AccordeonWrapper,
      SwitchWrapper
    },
    props: [
      'currentAsset',
      'tronSum',
      'isShowWithdrawal'
    ],
    emits: [
      commonEvents.assetChangeEvent,
      commonEvents.walletTopUpEvent,
      commonEvents.walletWithdrawalEvent
    ],
    mixins: [CurrencyRound],
    data () {
      return {
        CRYPTO_CURRENCYS_USDT,
        i18n,
        events: {
          assetChange: commonEvents.assetChangeEvent,
          walletTopUp: commonEvents.walletTopUpEvent,
          withdrawalFromWallet: commonEvents.walletWithdrawalEvent
        },
        isLoading: false,
        activeBalance: CRYPTO_CURRENCYS_USDT.USDTRC,
        pingEndpointTimer: {
          minutes: 1,
          seconds: 0,
          timerDone: false,
          timerInterval: null
        },
        hideZeroBalances: false
      }
    },
    computed: {
      ...mapGetters([
        vuexTypes.accountState,
        vuexTypes.accountBalances,
        vuexTypes.userTransferableTokens
      ]),
      formatedAsset () {
        return isNetworkToken(this.currentAsset) ? formatTokenCode(this.currentAsset) : ''
      },
      balance () {
        if (!isCryptoToken(this.currentAsset)) {
          return this.roundCurrency(
            get(this.accountBalances, `${this.currentAsset}.balance`) || 0, this.currentAsset
          )
        }
        return this.roundCurrency2(get(this.accountBalances, `${this.currentAsset}.balance`) || 0)
      },
      greyTronValue () {
        if (isCryptoToken(this.currentAsset) &&
          this.accountBalances[`${this.currentAsset}D`]
        ) {
          return this.roundCurrency2(this.accountBalances[`${this.currentAsset}D`].balance)
        }
        return this.roundCurrency2(0)
      },
      showedBalances () {
        const result = {}
        const hideZero = this.hideZeroBalances
        const balances = this.accountBalances

        sortAssetCodes(Object.keys(balances).filter(isNetworkToken))
          .forEach(function (el) {
            const balanceMain = +balances[el].balance
            const balanceD = balances[el + 'D'] ? +balances[el + 'D'].balance : 0
            const total = balanceMain + balanceD

            if (!hideZero || total !== 0) {
              result[el] = {
                key: el,
                balance: this.roundCurrency2(total)
              }
            }
          }, this)

        Object.keys(result).forEach(function (key) {
          result[key].formated = formatTokenCode(key)
        })

        return result
      },
      checkTransferable () {
        return !(this.userTransferableTokens.map(token => token.code)).includes(this.currentAsset)
      },
      isDisabledTransferableBtn () {
        return this.checkTransferable || this.accountState !== 'approved'
      }
    },
    methods: {
      ...mapActions({
        loadBalances: vuexTypes.GET_ACCOUNT_BALANCES
      }),
      toggleBalance (key) {
        this.activeBalance = this.activeBalance === key ? null : key
        this.setAsset(key)
      },
      setAsset (asset) {
        this.$emit(this.events.assetChange, asset)
      },
      async refreshWallet () {
        try {
          this.isLoading = true
          this.setAsset(this.currentAsset)
        } catch (e) {
          console.error(e)
        } finally {
          this.isLoading = false
          this.restartPingTimer()
        }
      },
      startPingTimer () {
        this.clearPingTimer()

        this.pingEndpointTimer.timerInterval = setInterval(async () => {
          if (this.pingEndpointTimer.seconds > 0) {
            this.pingEndpointTimer.seconds--
          } else if (this.pingEndpointTimer.minutes > 0) {
            this.pingEndpointTimer.minutes--
            this.pingEndpointTimer.seconds = 59
          } else {
            clearInterval(this.pingEndpointTimer.timerInterval)
            await this.loadBalances()
            this.restartPingTimer()
          }
        }, 1000)
      },
      restartPingTimer () {
        this.pingEndpointTimer.minutes = 1
        this.pingEndpointTimer.seconds = 0
        this.startPingTimer()
      },
      clearPingTimer () {
        if (this.pingEndpointTimer.timerInterval) {
          clearInterval(this.pingEndpointTimer.timerInterval)
        }
      }
    },
    mounted () {
      this.setAsset(this.CRYPTO_CURRENCYS_USDT.USDTRC)
      this.startPingTimer()
    },
    beforeDestroy () {
      this.clearPingTimer()
    }
  }
</script>

<style scoped lang="scss">
@import '~scss/variables';

.wallet-widget {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 15px;

  &__balance {
    display: flex;
    justify-self: center;
    align-items: center;
    width: 100%;
    padding: 10px;
    border-radius: 10px;
    background: $color-mint;
    min-height: 60px;

    p {
      width: 100%;
      font-weight: 700;
      font-size: 26px;
      line-height: 1;
      text-align: center;

      .currency {
        font-size: 18px;
      }
    }
  }

  .wallet-zero-switch {
    flex-direction: row-reverse;
    align-items: flex-end;
    width: 100% !important;

    /deep/ label.md-switch-label {
      padding-left: 0px !important;
      padding-right: 1rem !important;
    }
  }

  &__balances {
    display: flex;
    flex-direction: column;
    gap: 5px;
    max-height: 190px;
    overflow: hidden;
    overflow-y: auto;

    &-item {
      font-size: 18px;
      line-height: 1;
      font-weight: 400;
      color: $col-text;

      &.active {
        .wallet-widget__balances-item-header {
          background: $color-mint;
          border-bottom-left-radius: 0px;
          border-bottom-right-radius: 0px;
        }
      }

      &-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 4px 8px;
        border-radius: 4px;
        transition: background 0.1s ease-in-out;
        cursor: pointer;
        font-weight: 700;

        &:hover {
          background: $color-mint;
          opacity: 0.9;
        }
      }

      &-details {
        display: flex;
        flex-direction: column;
        gap: 8px;
        will-change: height, opacity, transform;
        border: 1px solid $color-mint;
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;
        padding: 6px 4px 10px;
        font-size: 16px;

        p {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0px 4px 0px 20px;
        }
      }
    }
  }

  &__btn-container {
    width: 100%;
    margin-top: auto;
    display: flex;
    justify-content: space-evenly;
    align-items: flex-start;
    gap: 20px;
  }
}
</style>



// WEBPACK FOOTER //
// src/vue/app/dashboard/widgets/WalletWidget.vue